import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Typography, Tabs, Tab, Snackbar, Alert } from '@mui/material';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { useTheme } from '../../context/ThemeContext';
import { usePermissions } from '../../context/PermissionsContext';
import {
  PARTNER_PERMISSIONS,
  PARTNER_PERMISSION_LABELS,
  PERMISSION_LABEL_FALLBACKS,
} from '../../constants/permissions';
import { PLATFORM_MCP_COPY } from '../../constants/platformMcpCopy';
import { TENANCY_COPY, USAGE_COPY } from '../../constants/partnerCopy';
import partnerAdminService from '../../services/partnerAdminService';
import companyAdminService from '../../services/companyAdminService';
import { describeApiError } from '../../utils/apiError';
import AuditPanel from '../PlatformAdmin/AuditPanel';
import UsageAnalyticsPanel from '../shared/UsageAnalytics/UsageAnalyticsPanel';
import PlatformMcpActivityTable from '../shared/PlatformMcpActivity/PlatformMcpActivityTable';
import PartnerOverviewPanel from './PartnerOverviewPanel';
import PartnerCompaniesPanel from './PartnerCompaniesPanel';
import PartnerUsersPanel from './PartnerUsersPanel';
import PartnerEntitlementsPanel from './PartnerEntitlementsPanel';
import PartnerLLMKeysPanel from './PartnerLLMKeysPanel';
import PartnerSharingPanel from './PartnerSharingPanel';
import styles from '../PlatformAdmin/PlatformAdmin.module.scss';

// Each tab is gated by its partner key (Partner tier guide §3.2). Overview needs
// any partner key, which the route already guarantees.
const TABS = [
  { value: 'overview', label: 'Overview', perm: null },
  { value: 'companies', label: 'Companies', perm: PARTNER_PERMISSIONS.COMPANIES },
  { value: 'users', label: 'Users', perm: PARTNER_PERMISSIONS.USERS },
  { value: 'limits', label: 'Feature limits', perm: PARTNER_PERMISSIONS.ENTITLEMENTS },
  { value: 'llmKeys', label: 'LLM keys', perm: PARTNER_PERMISSIONS.LLM_KEYS },
  { value: 'sharing', label: 'Sharing', perm: PARTNER_PERMISSIONS.SHARING },
  { value: 'usage', label: 'Usage', perm: PARTNER_PERMISSIONS.USAGE_ANALYTICS },
  { value: 'mcpActivity', label: PLATFORM_MCP_COPY.activityAdminMenu, perm: PARTNER_PERMISSIONS.USAGE_ANALYTICS },
  { value: 'audit', label: 'Audit log', perm: PARTNER_PERMISSIONS.AUDIT },
];

// "kb_dim1" → "Kb dim1" — last resort when no catalog label exists.
const humanizeKey = (key) => {
  const text = String(key || '').replace(/_/g, ' ');
  return text.charAt(0).toUpperCase() + text.slice(1);
};

// Module-level so AuditPanel sees a stable identity.
const fetchPartnerAuditLogs = (params) => partnerAdminService.listAuditLogs(params);

/**
 * Partner admin console (Partner tier guide §5) — the partner's staff manage
 * their client companies, users, feature limits, LLM keys, cross-company sharing,
 * usage and audit here. Every /partner-admin endpoint is scoped to the caller's
 * partner by the backend; another partner's company or user returns 404.
 *
 * The shell owns what several panels share (`ctx`): the partner (from
 * /partner-admin/me), its client companies (pickers + labels), permission labels,
 * and cross-tab navigation ("Add an admin for Company A" → Users).
 */
const PartnerAdmin = () => {
  const { isDarkMode } = useTheme();
  const { hasPartner, partner: sessionPartner, ready } = usePermissions();
  const [tab, setTab] = useState('overview');
  const [intent, setIntent] = useState(null); // { tab, ...payload } for cross-tab jumps
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  const [me, setMe] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [catalog, setCatalog] = useState([]);

  // Stable identity: panels use `notify` in useCallback/useEffect deps.
  const notify = useCallback(
    (message, severity = 'info') => setSnackbar({ open: true, message, severity }),
    []
  );

  const tabs = useMemo(
    () => (ready ? TABS.filter((t) => !t.perm || hasPartner(t.perm)) : []),
    [ready, hasPartner]
  );
  const activeTab = tabs.some((t) => t.value === tab) ? tab : tabs[0]?.value;

  useEffect(() => {
    if (tabs.length && !tabs.some((t) => t.value === tab)) {
      setTab(tabs[0].value);
    }
  }, [tabs, tab]);

  const muiTheme = useMemo(
    () =>
      createTheme({
        palette: { mode: isDarkMode ? 'dark' : 'light', primary: { main: '#e36250' } },
        typography: { fontFamily: '"Poppins", "sans-serif"' },
      }),
    [isDarkMode]
  );

  const reloadMe = useCallback(async () => {
    try {
      setMe(await partnerAdminService.getMe());
    } catch (err) {
      notify(describeApiError(err, 'Failed to load your partner.'), 'error');
    }
  }, [notify]);

  const canCompanies = ready && hasPartner(PARTNER_PERMISSIONS.COMPANIES);
  const reloadCompanies = useCallback(async () => {
    if (!canCompanies) return;
    try {
      const data = await partnerAdminService.listCompanies({ limit: 200 });
      setCompanies(data?.companies || (Array.isArray(data) ? data : []));
    } catch (err) {
      notify(describeApiError(err, 'Failed to load your companies.'), 'error');
    }
  }, [canCompanies, notify]);

  useEffect(() => {
    if (ready) reloadMe();
  }, [ready, reloadMe]);

  useEffect(() => {
    reloadCompanies();
  }, [reloadCompanies]);

  // Labels for company feature keys: the partner area can't read
  // /platform/permissions, but partner admins may read /company-admin/permissions
  // for their home company (§5.3). Failure just falls back to readable keys.
  useEffect(() => {
    if (!ready) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const data = await companyAdminService.getPermissions();
        const list = Array.isArray(data?.permissions) ? data.permissions : Array.isArray(data) ? data : [];
        if (!cancelled) setCatalog(list);
      } catch (err) {
        console.warn('Permission labels unavailable; showing keys', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [ready]);

  const partner = me?.partner || sessionPartner || null;
  const homeCompanyId = partner?.home_company_id ?? null;

  const ctx = useMemo(() => {
    const byKey = new Map(catalog.map((p) => [p.key, p]));
    const companiesById = new Map(companies.map((c) => [Number(c.id), c]));
    return {
      partner,
      partnerName: partner?.name || 'your partner',
      homeCompanyId,
      companies,
      reloadCompanies,
      reloadMe,
      planKeys: Array.isArray(me?.company_permission_keys) ? me.company_permission_keys : [],
      permissionEntry: (key) =>
        byKey.get(key) || {
          key,
          label: PARTNER_PERMISSION_LABELS[key] || PERMISSION_LABEL_FALLBACKS[key] || humanizeKey(key),
          description: '',
          category: 'Other',
        },
      companyLabel: (id, name) => {
        if (id == null) return '—';
        if (homeCompanyId != null && Number(id) === Number(homeCompanyId)) return TENANCY_COPY.directUsers;
        return companiesById.get(Number(id))?.name || name || `#${id}`;
      },
      navigate: (nextTab, payload = {}) => {
        setIntent({ tab: nextTab, ...payload });
        setTab(nextTab);
      },
    };
  }, [partner, homeCompanyId, companies, reloadCompanies, reloadMe, me, catalog]);

  const consumeIntent = useCallback(() => setIntent(null), []);
  const intentFor = (value) => (intent?.tab === value ? intent : null);

  // Usage: the partner's companies + its staff as "Direct users" (§5.7).
  const loadUsageCompanies = useCallback(async () => {
    const list = companies.map((c) => ({ id: c.id, name: c.name }));
    return homeCompanyId != null ? [{ id: homeCompanyId, name: TENANCY_COPY.directUsers }, ...list] : list;
  }, [companies, homeCompanyId]);

  const companyLabelFn = ctx.companyLabel;

  return (
    <ThemeProvider theme={muiTheme}>
      <Box className={styles.layout}>
        <Box className={styles.inner}>
          <Box className={styles.header}>
            <Box className={styles.titleSection}>
              <Typography variant="h4" component="h1">
                Partner Admin
              </Typography>
              <Typography variant="body2">
                {partner?.name
                  ? `Manage ${partner.name}'s companies, users, feature limits, keys, sharing and usage.`
                  : 'Manage your companies, users, feature limits, keys, sharing and usage.'}
              </Typography>
            </Box>
          </Box>

          <Tabs
            value={activeTab || false}
            onChange={(_, v) => { setIntent(null); setTab(v); }}
            className={styles.consoleTabs}
            TabIndicatorProps={{ style: { backgroundColor: '#e36250' } }}
            variant="scrollable"
            scrollButtons="auto"
          >
            {tabs.map((t) => (
              <Tab key={t.value} value={t.value} label={t.label} className={styles.tab} />
            ))}
          </Tabs>

          {activeTab === 'overview' && <PartnerOverviewPanel me={me} ctx={ctx} notify={notify} />}
          {activeTab === 'companies' && <PartnerCompaniesPanel ctx={ctx} notify={notify} />}
          {activeTab === 'users' && (
            <PartnerUsersPanel ctx={ctx} notify={notify} intent={intentFor('users')} onIntentConsumed={consumeIntent} />
          )}
          {activeTab === 'limits' && (
            <PartnerEntitlementsPanel ctx={ctx} notify={notify} intent={intentFor('limits')} />
          )}
          {activeTab === 'llmKeys' && <PartnerLLMKeysPanel ctx={ctx} notify={notify} />}
          {activeTab === 'sharing' && <PartnerSharingPanel ctx={ctx} notify={notify} />}
          {activeTab === 'usage' && (
            <UsageAnalyticsPanel
              basePath="/partner-admin/usage"
              scope="partner"
              notify={notify}
              loadCompanyOptions={loadUsageCompanies}
              companyLabelFn={companyLabelFn}
              redactedMessage={USAGE_COPY.partnerRedacted}
              emptyMessage={companies.length === 0 ? USAGE_COPY.partnerEmpty : null}
              dateTooltip={USAGE_COPY.attribution}
            />
          )}
          {activeTab === 'mcpActivity' && (
            <PlatformMcpActivityTable
              scope="partner"
              loadCompanyOptions={loadUsageCompanies}
              companyLabelFn={companyLabelFn}
            />
          )}
          {activeTab === 'audit' && (
            <AuditPanel
              notify={notify}
              fetchLogs={fetchPartnerAuditLogs}
              loadCompanyOptions={null}
              companyLabelFn={companyLabelFn}
            />
          )}
        </Box>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            sx={{ width: '100%', borderRadius: '10px' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </ThemeProvider>
  );
};

export default PartnerAdmin;
